/**
 * v2.10.1 — License-Details-Modal (MVP, DB-Daten only).
 *
 * Desktop (≥768 px): zentriert, max-width 480 px, Backdrop rgba(0,0,0,0.5).
 * Mobile (<768 px): Bottom-Sheet, full-width, max-height 70vh, slide-up.
 * ESC + Backdrop-Click schließen.
 *
 * Zeigt nur Daten aus der DB (license_tier, license_status, license_expires_at,
 * cancel_at_period_end, ablefy_subscription_id). „Abo verwalten" → /dashboard/abo.
 * Live-Ablefy-API-Calls kommen erst in v2.10.2.
 */
import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";

const TIER_LABEL = {
  free: "Free",
  // Sprint V93.1 (Mo 15.09.2026, Andreas-Direktive) — Keine Testphase mehr:
  // Legacy-`trial`-Tier wird UI-seitig komplett wie Pro Solo Monatlich angezeigt.
  trial: "Pro Solo · Monatlich",
  pro_solo_monthly: "Pro Solo · Monatlich",
  pro_solo_annual: "Pro Solo · Jährlich",
  pro_studio: "Pro Studio",
};

const TIER_PRICE = {
  pro_solo_monthly: "39 €/Monat",
  pro_solo_annual: "349 €/Jahr",
  pro_studio: "47 €/Monat",
};

const TIER_BADGE = {
  free: { bg: "#64748B", color: "#FFFFFF", label: "Free" },
  // Sprint V93.1 — Keine „Trial"-Optik mehr; Legacy-Tier faellt auf Pro-Solo-Optik.
  trial: { bg: "#10B981", color: "#FFFFFF", label: "Pro Solo aktiv" },
  pro_solo_monthly: { bg: "#10B981", color: "#FFFFFF", label: "Pro Solo aktiv" },
  pro_solo_annual: { bg: "#10B981", color: "#FFFFFF", label: "Pro Solo aktiv" },
  pro_studio: { bg: "#4563BB", color: "#FFFFFF", label: "Pro Studio aktiv" },
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const diff = d.getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

// v2.10.1 §M3 — Status-Text-Logik.
export function statusText({ licenseTier, licenseStatus, licenseExpiresAt, cancelAtPeriodEnd, trialEndsAt }) {
  if (licenseTier === "free") return "Free-Version · keine Laufzeit";
  if (licenseTier === "power_paket_onetime") return "Power-Paket · lebenslang";
  if (licenseStatus === "expired") {
    return `Abgelaufen am ${formatDate(licenseExpiresAt)}`;
  }
  // Sprint 5 S.2 — Trial-Branch entfernt; Trial-User sehen Pro-Solo-Status.
  if (licenseStatus === "cancelled" || cancelAtPeriodEnd) {
    return `Gekündigt · läuft noch bis ${formatDate(licenseExpiresAt)}`;
  }
  if (licenseStatus === "active") {
    return `Aktiv · läuft bis ${formatDate(licenseExpiresAt)}`;
  }
  return licenseStatus || "—";
}

function shortenSubId(id) {
  if (!id) return "—";
  const s = String(id);
  if (s.length <= 8) return s;
  return "…" + s.slice(-8);
}

export default function LicenseDetailsModal({ open, onClose, licenseData }) {
  const navigate = useNavigate();
  const closeBtnRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    // Fokus auf Close-Button für a11y
    closeBtnRef.current?.focus?.();
    // Body-Scroll blocken
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open || !licenseData) return null;

  const {
    license_tier: tier,
    license_status: status,
    license_expires_at: expiresAt,
    cancel_at_period_end: cancelEoP,
    ablefy_subscription_id: subId,
    trial_ends_at: trialEndsAt,
  } = licenseData;

  const badge = TIER_BADGE[tier] || TIER_BADGE.free;
  const tierLabel = TIER_LABEL[tier] || tier || "—";
  const price = TIER_PRICE[tier] || null;
  const text = statusText({
    licenseTier: tier,
    licenseStatus: status,
    licenseExpiresAt: expiresAt,
    cancelAtPeriodEnd: cancelEoP,
    trialEndsAt,
  });

  const handleVerwalten = () => {
    onClose();
    navigate("/dashboard/abo");
  };

  // Bug-Fix Mini-Hotfix 29.06.2026:
  // Parent `<header>` hat `backdrop-blur-xl`, was per CSS-Spec einen neuen
  // Containing-Block für descendant `fixed`-Elemente erzeugt. Ohne Portal
  // wurde das Modal in die 64 px hohe Header-Strip gequetscht („oberhalb
  // der Bildschirmgrenze verschoben"). Portal rendert direkt an body →
  // `fixed inset-0` bezieht sich wieder auf den Viewport.
  return createPortal(
    <div
      data-testid="license-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="license-modal-title"
    >
      <div
        data-testid="license-modal"
        className="
          bg-white w-full sm:w-[480px] sm:max-w-[480px]
          rounded-t-2xl sm:rounded-2xl
          shadow-2xl
          max-h-[70vh] sm:max-h-[85vh] overflow-y-auto
          transform transition-transform duration-200
          animate-[slideUp_200ms_ease-out]
        "
      >
        {/* Mobile Drag-Handle */}
        <div className="sm:hidden flex justify-center pt-3 pb-2" aria-hidden="true">
          <div
            data-testid="license-modal-drag-handle"
            className="w-10 h-1.5 rounded-full bg-slate-300"
          />
        </div>

        <div className="flex items-center justify-between px-6 pt-4 pb-3 sm:pt-6">
          <h2
            id="license-modal-title"
            data-testid="license-modal-title"
            className="text-lg font-bold text-[var(--fts-text-primary)]"
          >
            Dein Abo
          </h2>
          <button
            ref={closeBtnRef}
            data-testid="license-modal-close-btn"
            onClick={onClose}
            className="w-8 h-8 inline-flex items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 transition"
            aria-label="Schließen"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 pb-6 space-y-4">
          {/* Badge */}
          <div className="flex items-center gap-3">
            <span
              data-testid="license-modal-badge"
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold shadow-sm"
              style={{ backgroundColor: badge.bg, color: badge.color }}
            >
              {badge.label}
            </span>
            <span className="text-sm text-slate-500" data-testid="license-modal-tier-label">
              {tierLabel}
            </span>
          </div>

          {/* Status */}
          <div data-testid="license-modal-status-text" className="text-sm text-slate-700">
            {text}
          </div>

          <div className="border-t border-slate-100" />

          {/* Felder */}
          {price && (
            <div className="hidden sm:flex justify-between text-sm">
              <span className="text-slate-500">Laufzeit</span>
              <span
                data-testid="license-modal-price"
                className="text-slate-900 font-medium"
              >
                {price}
              </span>
            </div>
          )}

          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Nächste Abrechnung</span>
            <span
              data-testid="license-modal-next-billing"
              className="text-slate-900 font-medium"
            >
              {formatDate(expiresAt)}
            </span>
          </div>

          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Kündigung</span>
            <span
              data-testid="license-modal-cancel-status"
              className="text-slate-900 font-medium"
            >
              {status === "cancelled" || cancelEoP
                ? `Gekündigt zum ${formatDate(expiresAt)}`
                : "Nicht gekündigt"}
            </span>
          </div>

          {subId && (
            <div className="flex justify-between text-xs">
              <span className="text-slate-500">Subscription-ID</span>
              <span
                data-testid="license-modal-subscription-id"
                className="text-slate-700 font-mono"
              >
                {shortenSubId(subId)}
              </span>
            </div>
          )}

          {/* CTA */}
          <button
            type="button"
            onClick={handleVerwalten}
            data-testid="license-modal-manage-btn"
            className="
              w-full mt-2 inline-flex items-center justify-center gap-2
              rounded-xl bg-[var(--fts-primary-deep)] text-white
              py-3 text-sm font-semibold
              hover:opacity-90 transition
            "
          >
            Abo verwalten
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
